import { Text, Badge } from '@components/base/display';
import { VStack, HStack, Grid, Panel, Divider } from '@components/base/layout';
import { PropsPlayground } from './shared/PropsPlayground';
import { AccessibilityPanel } from './shared/AccessibilityPanel';
import { categories, sections, getShowcasesByCategory } from '../showcases';
import type { ShowcaseDefinition } from '../showcases/types';

interface ComponentExplorerContentProps {
  showcase?: ShowcaseDefinition;
  playgroundProps: Record<string, unknown>;
  isDarkMode: boolean;
  onPropsChange: (props: Record<string, unknown>) => void;
  onPropsReset: () => void;
}

export function ComponentExplorerContent({
  showcase,
  playgroundProps,
  isDarkMode,
  onPropsChange,
  onPropsReset,
}: Readonly<ComponentExplorerContentProps>) {
  if (!showcase) {
    return <WelcomeContent />;
  }

  const ShowcaseComponent = showcase.component;
  const category = categories.find((c) => c.id === showcase.category);

  return (
    <VStack gap={6}>
      {/* Component header */}
      <VStack gap={2}>
        <HStack gap={3} align="center">
          <Text as="h1" size="2xl" weight="bold">
            {showcase.name}
          </Text>
          {category && (
            <Badge size="sm" variant="primary" pill>
              {category.label}
            </Badge>
          )}
        </HStack>
        <Text color="secondary">
          {showcase.description}
        </Text>
      </VStack>

      <Divider spacing={0} color="light" />

      {/* Props playground */}
      <PropsPlayground
        propsDefinition={showcase.propsDefinition}
        values={playgroundProps}
        onChange={onPropsChange}
        onReset={onPropsReset}
      />

      {/* Showcase content */}
      <ShowcaseComponent
        playgroundProps={playgroundProps}
        isDarkMode={isDarkMode}
      />

      {/* Accessibility panel */}
      <AccessibilityPanel accessibility={showcase.accessibility} />
    </VStack>
  );
}

function WelcomeContent() {
  return (
    <VStack gap={8}>
      <VStack gap={2}>
        <Text as="h1" size="2xl" weight="bold">
          Component Explorer
        </Text>
        <Text color="secondary" size="lg">
          Interactive documentation for all base components. Explore props,
          variants, and accessibility features.
        </Text>
      </VStack>

      {sections.map((section) => {
        const sectionCategories = categories.filter(
          (c) => c.section === section.id,
        );

        return (
          <VStack key={section.id} gap={4}>
            <HStack gap={2} align="center">
              <Text as="h2" size="lg" weight="semibold">
                {section.label}
              </Text>
              <Badge size="sm" variant="default" pill>
                {sectionCategories.reduce(
                  (acc, cat) => acc + getShowcasesByCategory(cat.id).length,
                  0,
                )}
              </Badge>
            </HStack>

            {sectionCategories.length === 0 ? (
              <Panel variant="outlined" padding="default">
                <Text size="sm" color="muted" className="italic">
                  No components yet. Integrated components are coming soon.
                </Text>
              </Panel>
            ) : (
              <Grid columns={{ base: 2, md: 3, lg: 4 }} gap={4}>
                {sectionCategories.map((category) => {
                  const count = getShowcasesByCategory(category.id).length;
                  return (
                    <Panel
                      key={category.id}
                      variant="outlined"
                      padding="compact"
                    >
                      <VStack gap={1}>
                        <Text as="h3" size="sm" weight="semibold">
                          {category.label}
                        </Text>
                        <Text size="xs" color="muted">
                          {count} {count === 1 ? 'component' : 'components'}
                        </Text>
                      </VStack>
                    </Panel>
                  );
                })}
              </Grid>
            )}
          </VStack>
        );
      })}

      <Panel variant="outlined" padding="default">
        <VStack gap={2}>
          <Text as="h2" size="base" weight="semibold">
            Getting Started
          </Text>
          <Text size="sm" color="secondary">
            Select a component from the sidebar to view its interactive
            documentation. Toggle between light and dark mode using the
            theme switch, and experiment with props using the Props Playground.
          </Text>
        </VStack>
      </Panel>
    </VStack>
  );
}
